// Line-count audit (2026-05-04): 281 lines.
import { useCallback } from 'react';
import { ComponentTypeDetector } from '@/services/ai/componentTypeDetector';
import { ComponentType } from '@/types';
import type { PropertyMessage } from '@/components/ai/types';
import type {
  UseSlashCommandCreationDeps,
  UseSlashCommandCreationReturn,
} from './agentMessagingTypes';
import { getBlockedAICommandPermissionMessage } from '@/services/ai/agentCommandPermissions';
import {
  getSlashCommandMutationCategories,
  getSlashCommandMutationTargets,
} from './slashCommandPermission';

function appendMessage(previous: PropertyMessage[], message: PropertyMessage): PropertyMessage[] {
  return [...previous, message];
}

export function useSlashCommandCreation(
  deps: UseSlashCommandCreationDeps
): UseSlashCommandCreationReturn {
  const {
    component,
    multipleComponents,
    isCanvas,
    executeSlashCommand,
    setMessages,
    setInputValue,
    setIsLoading,
    setError,
    persistMessage,
    generateMessageId,
    applyPropertyChanges,
    commandPermissionGuard,
  } = deps;

  const handleNaturalLanguageCreationCommand = useCallback(
    async (userInput: string): Promise<{ handled: boolean }> => {
      const lowerInput = userInput.toLowerCase().trim();

      const convertToSlashCommand = (message: string): string | null => {
        const lower = message.toLowerCase().trim();

        if (/^(?:navigate|nav|go)\s*to\b/i.test(lower)) {
          return null;
        }

        const multiComponentPattern = /^(?:add|create|make|insert|put)\s+(?:a\s+|an\s+)?(?:new\s+)?.+(?:,\s*(?:a\s+|an\s+)?(?:new\s+)?.+\s+and\s+|,\s*(?:a\s+|an\s+)?(?:new\s+)?.+,\s*|\s+and\s+(?:a\s+|an\s+)?(?:new\s+)?.+(?:chart|text|button|image|input|container|table|gauge))/i;
        if (multiComponentPattern.test(lower)) {
          return null;
        }

        const isChartSelected = component?.type?.includes('chart');
        const chartProperties = component?.properties ?? {};

        const isModificationPhrase = /^(?:make\s+(?:it|this)|change\s+(?:it|this)?\s*to|convert\s+to|switch\s+to|turn\s+(?:it|this)\s+into)/i.test(lower);
        const isPropertyModification = /^(?:set|change|update|modify|adjust|increase|decrease|make)\s+(?:the\s+)?(?:max|min|width|height|size|color|background|border|padding|margin|font|opacity|flex)/i.test(lower);
        const isExplicitNavigationCommand =
          /\b(?:set|change|update|modify)\s+(?:default\s+)?navigation\s+to\b/i.test(lower) ||
          /\b(?:navigate|go|link|connect)\s+to\b/i.test(lower) ||
          /\bdrill[\s-]?down\s+to\b/i.test(lower);
        const isNavigationModification =
          isExplicitNavigationCommand ||
          (
            /\b(navigation|navigate|link|linked|connect|clickable|drill[\s-]?down|target)\b/i.test(lower)
            && /\b(page|screen|view)\b/i.test(lower)
          );

        if (isChartSelected && isModificationPhrase) {
          return null;
        }

        if (isPropertyModification) {
          return null;
        }

        if (component && isNavigationModification) {
          return null;
        }

        const isLayoutOrResponsiveRequest =
          /\b(row|column|grid|layout|align|wrap|nowrap|breakpoint|desktop|tablet|mobile|side by side|same row|single row|one row)\b/i.test(lower)
          && /\b(card|cards|kpi|metric|container|section|component|components|widget|widgets)\b/i.test(lower);
        if (isLayoutOrResponsiveRequest) {
          return null;
        }

        const chartDiscoveryPatterns = [
          /what.*(data|field|metric).*(chart|graph|gauge|meter)/i,
          /what can i.*(put|show|display).*(chart|graph|gauge|meter)/i,
          /what.*(chart|graph|gauge|meter).*(show|display)/i,
          /show me.*(chart|graph|gauge|meter).*(data|field|option)/i,
          /(chart|graph|gauge|meter).*(data|field|metric|source|option)/i,
          /(chart|graph|gauge|meter).*(suitable|available|compatible)/i,
          /suitable.*(chart|graph|gauge|meter)/i,
          /available.*(chart|graph|gauge|meter).*(data|field)/i,
          /data.*for.*(chart|graph|gauge|meter)/i,
          /field.*for.*(chart|graph|gauge|meter)/i,
          /numeric.*(field|metric).*(chart|graph|gauge|meter)/i,
          /find.*(data|field).*(chart|graph|gauge|meter)/i,
          /list.*(field|metric).*(chart|graph|gauge|meter)/i,
          /^what\s+(can|should|could|would)\s+.*(chart|graph|gauge|visualize|plot)/i,
          /what.*(schemas?|data).*(good|work|suitable|best).*(chart|graph|gauge)/i,
          /what.*(good|best|suitable).*(schemas?|data).*(chart|graph|gauge)/i,
          /which.*(schemas?|data).*(good|work|suitable|best).*(chart|graph|gauge)/i,
          /which.*(good|best|suitable).*(schemas?|data).*(chart|graph|gauge)/i,
          /(schemas?|data).*(good|suitable|best|work).*(chart|graph|gauge)/i,
          /recommend.*(data|schema).*(chart|graph|gauge)/i,
          /what.*(schema|data).*(chart|graph)/i,
          /what.*(chart|graph).*(schema|data)/i,
        ];
        if (chartDiscoveryPatterns.some((pattern) => pattern.test(lower))) {
          return null;
        }

        const componentMatch = lower.match(
          /^(?:add|create|make|insert|put)\s+(?:a\s+|an\s+)?(?:new\s+)?(text|button|image|input|icon|container|table|list)(?:\s+component|\s+here)?$/i
        );
        if (componentMatch) {
          return `/add ${componentMatch[1]}`;
        }

        if (/^(?:add|create|make)\s+(?:a\s+)?(?:new\s+)?flex(?:\s+container)?$/i.test(lower)) {
          return '/add flex';
        }

        const chartMatch = lower.match(
          /^(?:add|create|make|insert|put)\s+(?:a\s+|an\s+)?(?:new\s+)?(line|bar|pie|area|scatter|heatmap|treemap|gauge|map)\s+chart$/i
        );
        if (chartMatch) {
          return `/add ${chartMatch[1]} chart`;
        }

        const chartTypeOnlyMatch = lower.match(
          /^(?:add|create|make|insert|put)\s+(?:a\s+|an\s+)?(?:new\s+)?(treemap|heatmap|gauge)$/i
        );
        if (chartTypeOnlyMatch) {
          return `/add ${chartTypeOnlyMatch[1]} chart`;
        }

        const dataBoundPatterns = [
          /\bshowing\b/i,
          /\bdisplaying\b/i,
          /\bcomparing\b/i,
          /\bwith\s+data\b/i,
          /\bfor\s+\w+\s+(data|readings?|sensor|values?|metrics?)\b/i,
          /\b(connect|bind|link)(ed)?\s+(this|the)?\s*(chart|graph)?\s*to\b/i,
          /\bto\s+\w+\s+data\b/i,
          /\bfrom\s+\w+\s+(data|schema|source)\b/i,
          /\bfrom\s+[A-Z][a-zA-Z\s]+$/i,
          /\b(temperature|humidity|pressure|voltage|current|power|efficiency|level|sensor)\s*(data|readings?)?\b/i,
          /\b\w+_\w+\b/i,
          /\bof\s+\w+\s+by\s+\w+/i,
        ];
        const isDataBoundRequest = dataBoundPatterns.some((pattern) => pattern.test(message));

        const componentKeywords = ['chart', 'text', 'button', 'image', 'input', 'container', 'table', 'gauge', 'map'];
        const totalKeywordCount = componentKeywords.reduce((count, keyword) => {
          const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
          return count + (lower.match(regex) || []).length;
        }, 0);
        if (totalKeywordCount >= 2 && (/,/.test(lower) || /\band\b/.test(lower))) {
          return null;
        }

        const detectionResult = ComponentTypeDetector.detect(message);
        if (detectionResult.confidence >= 0.6 && !isDataBoundRequest) {
          switch (detectionResult.detectedType) {
            case 'vertical-gauge-chart':
              return '/add vertical-gauge chart';
            case 'gauge-chart':
              return '/add gauge chart';
            case 'line-chart':
              return '/add line chart';
            case 'bar-chart':
              return '/add bar chart';
            case 'pie-chart':
              return '/add pie chart';
            case 'area-chart':
              return /\bstacked\b/i.test(message) ? '/add area chart stacked' : '/add area chart';
            case 'scatter-chart':
              return '/add scatter chart';
            case 'heatmap-chart':
              return '/add heatmap chart';
            case 'treemap-chart':
              return '/add treemap chart';
            default:
          }
        }

        if (/(?:make|enable|add)\s+(?:this\s+)?(?:a\s+)?(?:refresh|live|update)\s*(?:badge|button)/i.test(lower)) {
          return '/refresh-badge';
        }

        return null;
      };

      const isMapComponent = component?.type === ComponentType.MAP_CHART;
      const mapCommandPatterns = [
        /(?:create|add|new|delete|remove)\s+(?:a\s+)?(?:area\s+)?group/i,
        /(?:show|hide)\s+group/i,
        /(?:filter\s+to|show\s+only)\s+group/i,
        /(?:place|add|put|create)\s+(?:a\s+)?(?:new\s+)?pin/i,
        /(?:cancel|exit|stop)\s+(?:pin|placement|placing)/i,
        /(?:show|hide)\s+(?:all\s+)?pins?/i,
        /(?:delete|remove)\s+pin/i,
        /(?:link|connect)\s+(?:this\s+)?pin/i,
        /(?:enable|disable|activate|deactivate)\s+pin\s+(?:navigation|clicking)/i,
        /(?:make)\s+pins?\s+(?:clickable|navigable)/i,
        /(?:set\s+)?(?:pin\s+)?colou?r\s+(?:to\s+)?\w+/i,
        /(?:use|set)\s+(?:pin\s+)?(?:\w+\s+)?icon/i,
        /(?:make)\s+pin\s+(?:red|blue|green|yellow|purple|pink|orange|cyan|white|black)/i,
      ];
      const isMapCommand = isMapComponent && mapCommandPatterns.some((pattern) => pattern.test(lowerInput));

      const slashEquivalent = isMapCommand ? null : convertToSlashCommand(lowerInput);
      if (!slashEquivalent) {
        return { handled: false };
      }

      const userMessage: PropertyMessage = {
        id: generateMessageId(),
        content: userInput,
        role: 'user',
        timestamp: new Date(),
      };
      setMessages((previous) => appendMessage(previous, userMessage));
      void persistMessage(userMessage);
      setInputValue('');
      setIsLoading(true);

      try {
        const targetComponent = component ?? (multipleComponents?.[0] ?? null);
        const slashCategories =
          /^\/(?:add|insert|create|template|tpl|duplicate|dup|\+)\b/i.test(slashEquivalent)
            ? ['insertion' as const]
            : [];
        const permissionResult = commandPermissionGuard?.({
          categories: slashCategories,
          targetComponents: targetComponent ? [targetComponent] : [],
          isCanvasMutation: isCanvas,
        });

        if (permissionResult && !permissionResult.allowed) {
          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: getBlockedAICommandPermissionMessage(permissionResult, 'insertion'),
            role: 'assistant',
            timestamp: new Date(),
            applied: false,
          };
          setMessages((previous) => appendMessage(previous, assistantMessage));
          void persistMessage(assistantMessage);
          setIsLoading(false);
          return { handled: true };
        }

        const result = await executeSlashCommand(slashEquivalent, targetComponent, isCanvas);
        const resultTargets = getSlashCommandMutationTargets(result, targetComponent);
        const resultPermission = commandPermissionGuard?.({
          categories: getSlashCommandMutationCategories(result, {
            isCanvasMutation: isCanvas && !targetComponent,
          }),
          targetComponents: resultTargets.targetComponents,
          targetComponentIds: resultTargets.targetComponentIds,
          isCanvasMutation: isCanvas && !targetComponent,
        });

        if (resultPermission && !resultPermission.allowed) {
          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: getBlockedAICommandPermissionMessage(resultPermission, 'content'),
            role: 'assistant',
            timestamp: new Date(),
            applied: false,
          };
          setMessages((previous) => appendMessage(previous, assistantMessage));
          void persistMessage(assistantMessage);
          setIsLoading(false);
          return { handled: true };
        }

        const assistantMessage: PropertyMessage = {
          id: generateMessageId(),
          content: result.message,
          role: 'assistant',
          timestamp: new Date(),
          propertyChanges: result.propertyChanges,
        };
        setMessages((previous) => appendMessage(previous, assistantMessage));
        void persistMessage(assistantMessage);

        if (result.propertyChanges && targetComponent) {
          applyPropertyChanges(generateMessageId(), result.propertyChanges);
        }
        result.applyMutations?.();
      } catch {
        setError('Failed to create component');
        const errorMessage: PropertyMessage = {
          id: generateMessageId(),
          content: `Failed to create component. Please try using the slash command: ${slashEquivalent}`,
          role: 'assistant',
          timestamp: new Date(),
        };
        setMessages((previous) => appendMessage(previous, errorMessage));
        void persistMessage(errorMessage);
      }

      setIsLoading(false);
      return { handled: true };
    },
    [
      component,
      multipleComponents,
      executeSlashCommand,
      isCanvas,
      setMessages,
      persistMessage,
      setInputValue,
      setIsLoading,
      applyPropertyChanges,
      generateMessageId,
      setError,
      commandPermissionGuard,
    ]
  );

  return { handleNaturalLanguageCreationCommand };
}
